import { forwardRef, useRef, useState } from 'react'
import { isMobile } from 'react-device-detect'
import ReactDatePicker from 'react-datepicker'
import { format, isBefore, startOfDay } from 'date-fns'
import type { FieldError } from 'libs/form'
import { useTranslation } from 'libs/i18n'
import { FormError, Label } from 'shared/atoms'
import { WithClassName } from 'shared/types/general.types'
import { cn } from 'utils/style'
import {
	CustomDatePickerButton,
	DatePickerClearButton,
	DatePickerVariant
} from './Button/DatepickerButton'
import { calendarProps, CalendarProvider } from './Calendar/Calendar'
import { getDateLocale } from './utils/date'

type DatePickerProps = WithClassName<{
	label: string
	selected: any
	disabled?: boolean
	required?: boolean
	minDate?: Date | undefined
	variant?: DatePickerVariant
	error?: FieldError | undefined
	onChange: (data: any) => void
}>

export const DatePicker = forwardRef(
	(
		{
			label,
			selected,
			className,
			disabled = false,
			required = false,
			minDate = undefined,
			variant = 'form',
			error,
			onChange
		}: DatePickerProps,
		ref: any
	) => {
		const { t, i18n } = useTranslation()
		const locale = getDateLocale(i18n.language)
		const pickerRef = useRef<ReactDatePicker | null>(null)
		const [isOpen, setIsOpen] = useState(false)
		const showClear = !isMobile && !!selected && !disabled
		const today = startOfDay(new Date())
		const isTodayDisabled = !!minDate && isBefore(today, startOfDay(minDate))

		const setRefs = (instance: ReactDatePicker | null) => {
			pickerRef.current = instance

			if (typeof ref === 'function') ref(instance)
			else if (ref) ref.current = instance
		}

		const clear = () => onChange(null)

		const close = () => {
			pickerRef.current?.setOpen(false, true)
			pickerRef.current?.setFocus()
		}

		const selectToday = () => {
			onChange(today)
			close()
		}

		return (
			<div className={cn('flex flex-col gap-1.5', className)}>
				<Label text={label} required={required} />
				<div className="relative">
					<CalendarProvider
						value={{
							summary: selected
								? format(selected, 'EEEE, d MMMM yyyy', { locale })
								: t('No date chosen'),
							actionLabel: t('Today'),
							isActionDisabled: isTodayDisabled,
							onAction: selectToday,
							onClear: clear
						}}>
						<ReactDatePicker
							{...calendarProps}
							ref={setRefs}
							locale={locale}
							selected={selected}
							minDate={minDate}
							disabled={disabled}
							withPortal={isMobile}
							customInput={
								<CustomDatePickerButton
									startDate={selected}
									variant={variant}
									disabled={disabled}
									isOpen={isOpen}
									hasClearButton={showClear}
									hasError={!!error}
								/>
							}
							onChange={onChange}
							onSelect={close}
							onInputClick={() => {
								if (isOpen) close()
							}}
							onCalendarOpen={() => setIsOpen(true)}
							onCalendarClose={() => setIsOpen(false)}
						/>
					</CalendarProvider>
					{showClear && (
						<DatePickerClearButton label={t('Clear date')} variant={variant} onClick={clear} />
					)}
				</div>
				<FormError text={error?.message} />
			</div>
		)
	}
)
